import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  RetireDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type RetireDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderDateControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'

export { RetireDeviceFormSchema, type RetireDeviceFormData } from './schema'

export type RetireDeviceComposerProps = {
  deviceNumber: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof RetireDeviceFormData, value: unknown): string | null => {
  const shape = RetireDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function RetireDeviceComposer(props: RetireDeviceComposerProps) {
  const { deviceNumber, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber })
  const deviceData = deviceDataRaw?.content

  const upsertDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => onError?.(error),
  })

  const form = useAppForm<RetireDeviceFormData>({
    schema: RetireDeviceFormSchema,
    defaultValues: {
      deviceRetirementDate: new Date().toISOString().split('T')[0],
    },
    onSubmit: async ({ value }) => {
      const formValue = value as RetireDeviceFormData
      if (isDeviceLoading) return

      const payload = transformToApiRequest(formValue, deviceNumber) as any
      const existingDevice = deviceData?.device || {}
      const existingRetirementInfo = deviceData?.retirementInfo || {}

      const mergedPayload = {
        ...payload,
        device: {
          ...existingDevice,
          ...payload.device,
          deviceNumber,
        },
        retirementInfo: {
          ...existingRetirementInfo,
          ...payload.retirementInfo,
          deviceNumber,
          deviceRetirementInfoNumber: (existingRetirementInfo as any)?.deviceRetirementInfoNumber,
        },
      }

      await upsertDevice.mutateAsync(mergedPayload)
      onSuccess?.()
    },
  })

  const renderControl = (control: ControlConfig) => {
    if (control.htmlControl !== 'date') return null

    return (
      <form.AppField
        key={control.name}
        name={control.name as 'deviceRetirementDate'}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof RetireDeviceFormData, value) || undefined,
        }}
      >
        {(bound) =>
          renderDateControl(
            {
              ...control,
              required: true,
            },
            bound,
          )
        }
      </form.AppField>
    )
  }

  const renderFormContent = () =>
    groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map((control) => (
            <div key={control.name}>{renderControl(control)}</div>
          ))}
        </div>
      )
    })

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            {FORM_CONFIG.formDescription && (
              <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
            )}
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}
          {renderButtonControl(
            {
              label: upsertDevice.isPending
                ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Retiring...')
                : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Retire'),
              variant: 'default',
              type: 'submit',
            },
            { SubmitButton: form.SubmitButton },
          )}
        </div>
      </div>
    </form.AppForm>
  )
}
